import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CheckCircle, XCircle, AlertTriangle, Clock, DollarSign,
    ChevronLeft, ChevronRight, Calendar, CreditCard, User, Building2,
    Home, TrendingUp, TrendingDown, Plus, Minus, Mail
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from './Common/LoadingSpinner';
import Modal from './Common/Modal';

const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'KES 0';
    return `KES ${parseFloat(amount).toLocaleString()}`;
};

const getPeriodStatusStyle = (status) => {
    switch (status) {
        case 'paid':
            return {
                dot: 'bg-green-500',
                bg: 'bg-green-50',
                border: 'border-green-400',
                text: 'text-green-700',
                badge: 'bg-green-100 text-green-800',
                icon: 'text-green-500',
                label: 'Paid',
            };
        case 'unpaid':
            return {
                dot: 'bg-red-500',
                bg: 'bg-red-50',
                border: 'border-red-400',
                text: 'text-red-700',
                badge: 'bg-red-100 text-red-800',
                icon: 'text-red-500',
                label: 'Unpaid',
            };
        case 'partial':
            return {
                dot: 'bg-yellow-500',
                bg: 'bg-yellow-50',
                border: 'border-yellow-400',
                text: 'text-yellow-700',
                badge: 'bg-yellow-100 text-yellow-800',
                icon: 'text-yellow-500',
                label: 'Partial',
            };
        case 'not_due':
            return {
                dot: 'bg-blue-400',
                bg: 'bg-blue-50',
                border: 'border-blue-300',
                text: 'text-blue-600',
                badge: 'bg-blue-100 text-blue-700',
                icon: 'text-blue-400',
                label: 'Not due yet',
            };
        default:
            return {
                dot: 'bg-gray-300',
                bg: 'bg-gray-50',
                border: 'border-gray-300',
                text: 'text-gray-500',
                badge: 'bg-gray-100 text-gray-600',
                icon: 'text-gray-400',
                label: status || 'Unknown',
            };
    }
};

const PayButton = ({ period, leaseId, onPaymentInitiated }) => {
    const [paying, setPaying] = useState(false);

    const handlePay = async (e) => {
        e.stopPropagation();
        setPaying(true);
        try {
            // Create a cash payment for this period
            const payload = {
                lease_id: leaseId,
                amount: period.expected_amount,
                payment_type: 'rent',
                payment_method: 'cash',
                payment_period_start: period.period_start,
                payment_period_end: period.period_end,
                metadata: { 
                    notes: `Manual payment for ${period.label}`,
                    source: 'period_history_modal'
                }
            };
            await api.post('/payments', payload);
            if (onPaymentInitiated) onPaymentInitiated(period.key);
        } catch (error) {
            console.error('Payment failed:', error);
            alert('Failed to record payment: ' + (error.response?.data?.error || error.message));
        } finally {
            setPaying(false);
        }
    };

    return (
        <button
            onClick={handlePay}
            disabled={paying}
            className="flex items-center px-3 py-1.5 text-xs font-medium rounded-lg
                bg-indigo-600 text-white hover:bg-indigo-700 
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
            {paying ? (
                <>
                    <svg className="animate-spin h-3.5 w-3.5 mr-1" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                </>
            ) : (
                <>
                    <DollarSign className="h-3.5 w-3.5 mr-1" />
                    Pay {formatCurrency(period.expected_amount)}
                </>
            )}
        </button>
    );
};

// ── Consecutive unpaid counter ─────────────────────────────────────────────
const UnpaidStreak = ({ periods }) => {
    let maxStreak = 0;
    let currentStreak = 0;
    for (const p of periods) {
        if (p.is_past_period && (p.status === 'unpaid' || p.status === 'partial')) {
            currentStreak++;
            maxStreak = Math.max(maxStreak, currentStreak);
        } else {
            currentStreak = 0;
        }
    }

    if (maxStreak <= 0) return null;

    const dangerLevel = maxStreak >= 4 ? 'bg-red-600' : maxStreak >= 2 ? 'bg-orange-500' : 'bg-yellow-500';

    return (
        <div className={`${dangerLevel} text-white px-3 py-2 rounded-lg flex items-center space-x-2 text-sm`}>
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>
                <strong>{maxStreak}</strong> consecutive unpaid period{maxStreak > 1 ? 's' : ''}
                {maxStreak >= 4 ? ' — Risk of eviction' : maxStreak >= 3 ? ' — High risk' : ''}
            </span>
        </div>
    );
};

// ── Status Summary Bar ─────────────────────────────────────────────────────
const StatusBar = ({ periods }) => {
    const total = periods.length;
    const paid = periods.filter(p => p.status === 'paid').length;
    const unpaid = periods.filter(p => p.status === 'unpaid' || p.status === 'partial').length;
    const notDue = periods.filter(p => p.status === 'not_due').length;

    if (total === 0) return null;

    const paidPct = (paid / total) * 100;
    const unpaidPct = (unpaid / total) * 100;
    const notDuePct = (notDue / total) * 100;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Collection Rate: <strong>{Math.round(paidPct)}%</strong></span>
                <span>{paid} paid &middot; {unpaid} unpaid &middot; {notDue} not due</span>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden flex">
                {paidPct > 0 && (
                    <div className="h-full bg-green-500 transition-all" style={{ width: `${paidPct}%` }} title={`${paid} paid`} />
                )}
                {unpaidPct > 0 && (
                    <div className="h-full bg-red-500 transition-all" style={{ width: `${unpaidPct}%` }} title={`${unpaid} unpaid`} />
                )}
                {notDuePct > 0 && (
                    <div className="h-full bg-blue-400 transition-all" style={{ width: `${notDuePct}%` }} title={`${notDue} not due`} />
                )}
            </div>
        </div>
    );
};

// ── Main Modal Component ───────────────────────────────────────────────────
const PaymentPeriodHistoryModal = ({ isOpen, onClose, leaseId, tenantData }) => {
    const [loading, setLoading] = useState(true);
    const [leaseInfo, setLeaseInfo] = useState(null);
    const [periods, setPeriods] = useState([]);
    const [allPeriods, setAllPeriods] = useState([]);
    const [pagination, setPagination] = useState({ offset: 0, limit: 12, total: 0, has_more: false, remaining: 0 });
    const [loadingMore, setLoadingMore] = useState(false);

    const fetchPeriods = useCallback(async (offset = 0, append = false) => {
        if (!append) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await api.get(`/payments/lease/${leaseId}/period-history`, {
                params: { offset, limit: 12 }
            });

            const data = res.data?.data;
            if (data) {
                setLeaseInfo(data.lease);
                setPagination(data.pagination);
                if (append) {
                    setAllPeriods(prev => [...prev, ...data.periods]);
                } else {
                    setAllPeriods(data.periods);
                }
                setPeriods(data.periods);
            }
        } catch (error) {
            console.error('Error fetching period history:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [leaseId]);

    useEffect(() => {
        if (isOpen && leaseId) {
            fetchPeriods(0, false);
        }
    }, [isOpen, leaseId, fetchPeriods]);

    const handleLoadMore = () => {
        if (pagination.has_more && !loadingMore) {
            fetchPeriods(pagination.offset + pagination.limit, true);
        }
    };

    const handlePaymentInitiated = (periodKey) => {
        // Refresh the data to reflect new payment
        fetchPeriods(0, false);
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Payment Period History" size="xl">
            <div className="space-y-4">
                {/* ── Tenant / Lease Info ── */}
                {leaseInfo && (
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                {tenantData?.avatar ? (
                                    <img src={tenantData.avatar} alt="" className="h-10 w-10 rounded-full" />
                                ) : (
                                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                        <User className="h-5 w-5 text-indigo-600" />
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">{leaseInfo.tenant_name || 'Tenant'}</p>
                                    <p className="text-xs text-gray-500">
                                        {leaseInfo.asset_name} &middot; Unit {leaseInfo.unit_number} &middot; {leaseInfo.lease_number}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right text-xs text-gray-500">
                                <p>Rent: {formatCurrency(leaseInfo.rent_amount)}/{leaseInfo.billing_cycle === 'termly' ? 'term' : 'mo'}</p>
                                <p>{new Date(leaseInfo.start_date).toLocaleDateString()} — {leaseInfo.end_date ? new Date(leaseInfo.end_date).toLocaleDateString() : 'Ongoing'}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Consecutive Unpaid Alert ── */}
                <UnpaidStreak periods={allPeriods} />

                {/* ── Summary Bar ── */}
                <StatusBar periods={allPeriods} />

                {/* ── Periods Grid ── */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <LoadingSpinner size="lg" />
                    </div>
                ) : allPeriods.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No periods found for this lease</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Visual timeline legend */}
                        <div className="flex items-center space-x-4 text-xs text-gray-500 pb-2 border-b">
                            <span className="flex items-center"><span className="h-2.5 w-2.5 rounded-full bg-green-500 mr-1" /> Paid</span>
                            <span className="flex items-center"><span className="h-2.5 w-2.5 rounded-full bg-red-500 mr-1" /> Unpaid</span>
                            <span className="flex items-center"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500 mr-1" /> Partial</span>
                            <span className="flex items-center"><span className="h-2.5 w-2.5 rounded-full bg-blue-400 mr-1" /> Not due</span>
                        </div>

                        {allPeriods.map((period, idx) => {
                            const style = getPeriodStatusStyle(period.status);
                            const isPayable = (period.status === 'unpaid' || period.status === 'partial') && period.is_past_period;

                            return (
                                <div
                                    key={period.key}
                                    className={`flex items-center justify-between p-3 rounded-lg border-l-4 transition-all hover:shadow-sm ${
                                        style.bg
                                    } ${style.border} ${
                                        idx === 0 ? 'ring-2 ring-indigo-200 ring-offset-1' : ''
                                    }`}
                                >
                                    {/* Period info */}
                                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                                        {/* Timeline dot */}
                                        <div className={`h-3 w-3 rounded-full ${style.dot} flex-shrink-0`} />

                                        <div className="min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">{period.label}</span>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.badge}`}>
                                                    {style.label}
                                                </span>
                                                {period.is_past_period && period.status === 'not_due' && (
                                                    <span className="text-xs text-gray-400">(future)</span>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-3 mt-0.5 text-xs text-gray-500">
                                                <span>Expected: {formatCurrency(period.expected_amount)}</span>
                                                {period.paid_amount > 0 && (
                                                    <span className="text-green-600">
                                                        Paid: {formatCurrency(period.paid_amount)}
                                                    </span>
                                                )}
                                                {period.payment_count > 0 && (
                                                    <span>({period.payment_count} payment{period.payment_count > 1 ? 's' : ''})</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment status bar graphic */}
                                    <div className="hidden sm:flex items-center mx-4 flex-1 max-w-xs">
                                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                            {period.paid_amount > 0 && (
                                                <div
                                                    className={`h-full rounded-full transition-all ${
                                                        period.status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'
                                                    }`}
                                                    style={{
                                                        width: `${Math.min(100, (period.paid_amount / period.expected_amount) * 100)}%`
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <span className="ml-2 text-xs text-gray-500 w-12 text-right">
                                            {period.expected_amount > 0
                                                ? `${Math.round((period.paid_amount / period.expected_amount) * 100)}%`
                                                : '0%'}
                                        </span>
                                    </div>

                                    {/* Pay / Email button */}
                                    <div className="flex-shrink-0">
                                        {isPayable && (
                                            <PayButton
                                                period={period}
                                                leaseId={leaseId}
                                                onPaymentInitiated={handlePaymentInitiated}
                                            />
                                        )}
                                        {period.status === 'paid' && (
                                            <div className="flex items-center space-x-2">
                                                <span className="flex items-center text-xs text-green-600">
                                                    <CheckCircle className="h-4 w-4 mr-1" />
                                                    Paid
                                                </span>
                                                {period.payment_ids && period.payment_ids.length > 0 && (
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                await api.post(`/payments/${period.payment_ids[0]}/email-receipt`, {});
                                                                alert('Receipt sent successfully to tenant\'s email');
                                                            } catch (err) {
                                                                console.error('Failed to email receipt:', err);
                                                                alert('Failed to send receipt: ' + (err.response?.data?.error || err.message));
                                                            }
                                                        }}
                                                        className="flex items-center px-2 py-1 text-xs font-medium rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                                        title="Email Receipt"
                                                    >
                                                        <Mail className="h-3 w-3 mr-1" />
                                                        Email
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Load More */}
                        {pagination.has_more && (
                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className="flex items-center px-4 py-2 text-sm font-medium text-indigo-600 
                                        bg-indigo-50 rounded-lg hover:bg-indigo-100 
                                        disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loadingMore ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="h-4 w-4 mr-1" />
                                            Show {Math.min(12, pagination.remaining)} more periods ({pagination.remaining} remaining)
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PaymentPeriodHistoryModal;